/** @jsx jsx */
import * as React from 'react';
import {
  useCombobox,
  useMultipleSelection,
  UseMultipleSelectionProps,
} from 'downshift';
import { jsx, css } from '@emotion/core';
import cx from 'classnames';
import useOnClickOutside from 'use-onclickoutside';
import { OverflowList } from '@blueprintjs/core/lib/cjs/components/overflow-list/overflowList';
import { useDeepCompareEffect } from 'react-use';

import CloseOrb from '@clearkit/icons/glyphs/CloseOrb';
import Caret from '@clearkit/icons/glyphs/Caret';

import CKMultiPickerItem from './CKMultiPickerItem';

import { CKChip } from '../CKChip';
import { usePopover } from '../../hooks';
import {
  ItemBase,
  CommonSelectProps,
  CKPickerVariant,
} from '../../common/types';
import {
  defaultItemRenderer,
  defaultEmptyState,
  defaultOptionFilterFunc,
} from '../../common/utils';
import {
  ItemGroup,
  isItemGroupArray,
  CKOptionGroupsMenu,
} from '../CKOptionGroupsMenu';

export interface CKMultiPickerProps<T extends ItemBase>
  extends UseMultipleSelectionProps<T>,
    CommonSelectProps<T> {
  items: T[] | ItemGroup<T>[];
  isCreateable?: boolean;
  isDisabled?: boolean;
  isSmall?: boolean;
  variant?: CKPickerVariant;
  onCreateItem?: (item: T) => void;
  itemRenderer?: (item: T) => string;
  emptyState?: (inputValue: string) => React.ReactNode;
  optionFilterFunc?: (items: T[], inputValue: string) => T[];
}

export const CKMultiPicker = <T extends ItemBase>(
  props: CKMultiPickerProps<T>
): React.ReactElement<CKMultiPickerProps<T>> => {
  const {
    placeholder,
    leftIcon,
    itemRenderer = defaultItemRenderer,
    emptyState = defaultEmptyState,
    itemMetaRenderer = null,
    isItemDisabled = () => false,
    optionFilterFunc = defaultOptionFilterFunc,
    items,
    popperZIndex = 10,
    isCreateable,
    isDisabled = false,
    isSmall = false,
    onCreateItem,
    variant = 'default',
    selectedItems = [],
    itemToString,
    ...downshiftProps
  } = props;
  if (isItemGroupArray(items) && isCreateable) {
    throw new Error(
      'CKPicker does not support combining option groups and isCreatable behavior'
    );
  }

  function getItemsForSelection(input: T[] | ItemGroup<T>[]) {
    let results: T[] = [];
    if (isItemGroupArray(input)) {
      input.forEach(i => {
        results = results.concat(i.items);
      });
    } else {
      results = input;
    }

    return results;
  }

  function filterItems(filterString: string, items: T[] | ItemGroup<T>[]) {
    if (!filterString) return items;

    let filteredItems: T[] | ItemGroup<T>[];
    if (isItemGroupArray(items)) {
      filteredItems = items
        .map(section => {
          return {
            ...section,
            items: optionFilterFunc(section.items, filterString),
          };
        })
        .filter(section => section.items.length > 0);
    } else {
      filteredItems = optionFilterFunc(items, filterString);
    }
    return filteredItems;
  }

  const [isCreating, setIsCreating] = React.useState(false);
  const [inputValue, setInputValue] = React.useState('');

  const [inputItems, setInputItems] = React.useState<T[]>(
    getItemsForSelection(items)
  );
  const optionsForDisplay = isCreating
    ? inputItems
    : filterItems(inputValue, items);

  const popperReference = React.useRef<HTMLDivElement>(null);
  const popperPopover = React.useRef<HTMLDivElement>(null);
  const popper = usePopover({
    popperReference,
    popperPopover,
    popperOffset: props.popperOffset,
    popperPlacement: props.popperPlacement,
    popperModifiers: props.popperModifiers,
  });

  const {
    getSelectedItemProps,
    getDropdownProps,
    addSelectedItem,
    removeSelectedItem,
  } = useMultipleSelection({
    ...downshiftProps,
    selectedItems,
    // @ts-ignore
    stateReducer: (_, actionAndChanges) => {
      const { type, changes } = actionAndChanges;
      switch (type) {
        case useMultipleSelection.stateChangeTypes.FunctionRemoveSelectedItem:
          return {
            ...changes,
            activeIndex: null,
          };
        default:
          return changes;
      }
    },
  });

  const {
    isOpen,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    highlightedIndex,
    getItemProps,
    openMenu,
    closeMenu,
    selectItem,
    setHighlightedIndex,
  } = useCombobox({
    // @ts-ignore
    itemToString,
    inputValue,
    selectedItem: null,
    items: inputItems,
    onInputValueChange: ({ inputValue, selectedItem }) => {
      const filteredItems = filterItems(inputValue || '', items);

      if (isCreateable && isCreating && filteredItems.length > 0) {
        setIsCreating(false);
      }

      if (!selectedItem) {
        setInputItems(getItemsForSelection(filteredItems));
      }
    },
    stateReducer: (state, actionAndChanges) => {
      const { changes, type } = actionAndChanges;
      switch (type) {
        case useCombobox.stateChangeTypes.InputBlur:
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          return {
            ...changes,
            highlightedIndex: state.highlightedIndex,
            isOpen: true,
          };
        default:
          return changes;
      }
    },
    // @ts-ignore
    onStateChange: ({ inputValue, type, selectedItem: selected }) => {
      switch (type) {
        case useCombobox.stateChangeTypes.InputChange:
          setInputValue(inputValue || '');
          break;
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          if (selected) {
            if (selectedItemValues.includes(selected.value)) {
              removeSelectedItem(selected);
            } else {
              if (isCreateable && onCreateItem && isCreating) {
                onCreateItem(selected);
                setIsCreating(false);
                setInputItems(getItemsForSelection(items));
              } else {
                addSelectedItem(selected);
              }
            }

            // @ts-ignore
            selectItem(null);
            setInputValue('');
          }
          break;
        default:
          break;
      }
    },
  });

  React.useEffect(() => {
    if (isOpen && popper.current) {
      popper.current?.forceUpdate();
    }
  }, [isOpen, popper, selectedItems]);

  React.useEffect(() => {
    if (inputItems.length === 0 && isCreateable) {
      setIsCreating(true);
      // @ts-ignore
      setInputItems([{ label: `${inputValue}`, value: inputValue }]);
      setHighlightedIndex(0);
    }
  }, [
    inputItems,
    isCreateable,
    setIsCreating,
    setHighlightedIndex,
    inputValue,
  ]);

  useDeepCompareEffect(() => {
    setInputItems(getItemsForSelection(items));
  }, [items]);

  useOnClickOutside(popperReference, closeMenu);

  const outerPickerClass = cx('ck-picker', {
    'ck-picker--open': isOpen,
    'ck-picker--disabled': isDisabled,
    'ck-picker--small': isSmall,
    'ck-picker--error': variant === 'error',
    'ck-picker--success': variant === 'success',
  });

  const selectedItemValues = selectedItems.map(item => item.value);

  const renderListItem = (item: T, index: number) => {
    const itemProps = getItemProps({
      item,
      index,
      disabled: isItemDisabled(item),
    });
    return (
      <CKMultiPickerItem
        label={item.value}
        value={item.value}
        icon={item.icon}
        key={`${item.value}${index}`}
        isHighlighted={highlightedIndex === index}
        isSelected={selectedItemValues.includes(item.value)}
        isDisabled={isItemDisabled(item)}
        itemMetaRenderer={itemMetaRenderer ? itemMetaRenderer(item) : null}
        isCreating={isCreating}
        textToHighlight={itemRenderer(item)}
        inputValue={inputValue}
        itemProps={itemProps}
      />
    );
  };

  return (
    <div className={outerPickerClass} ref={popperReference}>
      <div
        {...getComboboxProps()}
        className="ck-picker-toggle ck-picker-toggle--typeable"
        css={css`
          min-height: 2.25rem;
        `}
      >
        <label className="ck-sr-only" {...getLabelProps()}>
          {placeholder}
        </label>

        {leftIcon && (
          <span className="ck-picker-icon ck-picker-icon--left">
            {leftIcon}
          </span>
        )}

        {selectedItems.length === 0 && !isOpen && (
          <span
            className="ck-picker-value ck-picker-value--placeholder"
            css={css`
              position: relative;
              left: -2px;
            `}
          >
            {placeholder}
          </span>
        )}

        {!isOpen && (
          <OverflowList
            collapseFrom="end"
            className="ck-picker-values"
            css={css`
              display: flex;
              flex-wrap: nowrap;
              overflow: hidden;
              position: relative;
              flex: 1;

              .bp3-overflow-list-spacer {
                flex-shrink: 1;
                width: 1px;
                pointer-events: none;
              }
            `}
            items={selectedItems}
            overflowRenderer={items => {
              return (
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                  `}
                >
                  <span
                    css={css`
                      pointer-events: none;
                    `}
                    className="ck-badge"
                  >
                    +{items.length} more
                  </span>
                </div>
              );
            }}
            visibleItemRenderer={(selectedItem, index) => {
              return (
                <CKChip
                  css={css`
                    z-index: 10;
                    ${isDisabled && 'pointer-events: none;'}
                  `}
                  key={`selected-item-${index}`}
                  {...getSelectedItemProps({
                    selectedItem,
                    index,
                    disabled: isDisabled,
                  })}
                  onClick={(e: any) => {
                    e.stopPropagation();
                    removeSelectedItem(selectedItem);
                  }}
                >
                  {selectedItem.label}
                </CKChip>
              );
            }}
          />
        )}

        <div className={isOpen ? 'ck-picker-values' : ''}>
          {isOpen &&
            selectedItems.map((selectedItem, index) => {
              return (
                <span
                  className="ck-chip"
                  key={`selected-item-${index}`}
                  {...getSelectedItemProps({ selectedItem, index })}
                >
                  {selectedItem.label}
                  <button
                    disabled={isDisabled}
                    type="button"
                    className="ck-chip-delete"
                    onClick={e => {
                      e.stopPropagation();
                      removeSelectedItem(selectedItem);
                    }}
                  >
                    <CloseOrb height={16} width={16} />
                  </button>
                </span>
              );
            })}
          <input
            disabled={isDisabled}
            className="ck-picker-value"
            css={
              isOpen
                ? css`
                    display: inline-block;
                    width: auto;
                  `
                : css`
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    width: 100%;
                    height: 100%;
                    opacity: 0;
                  `
            }
            {...getInputProps(
              getDropdownProps({
                placeholder,
                onClick: isOpen ? () => {} : openMenu,
                onFocus: isOpen ? () => {} : openMenu,
              })
            )}
          />
        </div>

        <button
          disabled={isDisabled}
          css={css`
            display: flex;
            margin-left: auto;
          `}
          {...getToggleButtonProps({ type: 'button' })}
          aria-label="toggle menu"
        >
          <span className="ck-picker-icon ck-picker-icon--right">
            <Caret />
          </span>
        </button>
      </div>
      <div
        css={css`
          visibility: ${isOpen ? 'visible' : 'hidden'};
          pointer-events: ${isOpen ? 'auto' : 'none'};
          z-index: ${popperZIndex};
        `}
        {...getMenuProps({ ref: popperPopover })}
        className="ck-popover ck-popover--full-width"
      >
        {!isOpen ? null : optionsForDisplay.length === 0 ? (
          emptyState(inputValue)
        ) : (
          <CKOptionGroupsMenu
            options={optionsForDisplay}
            renderItem={renderListItem}
            stickyGroups
          />
        )}
      </div>
    </div>
  );
};
